import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { DestroyRef, inject, Injectable, PLATFORM_ID } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { catchError, of } from "rxjs";
import { MarketingApiService } from "./marketing-api.service";
import { MarketingMeasurementService } from "./measurement.service";
import {
  isMarketingProvider,
  MarketingDestination,
  MarketingJourneyContext,
} from "./marketing.models";
import { MarketingJourneyService } from "./journey.service";
import { commerceContext, commercePayload } from "./commerce-payload";

import {
  BrowserPixelAdapter,
  GooglePixelAdapter,
  MetaPixelAdapter,
} from "./browser-pixel-adapters";

@Injectable({ providedIn: "root" })
export class MarketingPixelService {
  private readonly api = inject(MarketingApiService);
  private readonly internalJourney = inject(MarketingJourneyService);
  private readonly measurement = inject(MarketingMeasurementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly adapters: Record<"meta" | "google", BrowserPixelAdapter> = {
    meta: new MetaPixelAdapter(this.document),
    google: new GooglePixelAdapter(this.document),
  };
  private readonly emitted = new Set<string>();
  private activeEvent?: number;
  private generation = 0;
  private measurementGeneration = 0;
  private waiting: (() => void)[] = [];
  private hadSession = false;
  private readonly viewAttempts = new Set<string>();
  private readonly pendingOrders = new Set<number>();
  constructor() {
    this.measurement.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.hadSession || !this.measurement.advertising) this.generation++;
        this.measurementGeneration++;
        const waiting = this.waiting;
        this.waiting = [];
        if (this.measurement.advertising) {
          this.hadSession = true;
          for (const action of waiting) action();
        }
        if (!this.measurement.advertising) this.pendingOrders.clear();
      });
  }
  eventLoaded(eventId: number, context?: MarketingJourneyContext): void {
    this.activeEvent = eventId;
    this.journey(eventId, "event_viewed", context);
  }
  leaveEvent(): void {
    this.internalJourney.leaveEvent();
    this.activeEvent = undefined;
    this.generation++;
    this.viewAttempts.clear();
  }
  journey(
    eventId: number,
    event: "event_viewed" | "cart_item_added" | "checkout_started",
    context?: MarketingJourneyContext,
  ): void {
    const commerce = commerceContext(context);
    if (this.browser) this.measurement.ensure();
    this.internalJourney.record(eventId, event);
    if (
      this.browser &&
      Number.isSafeInteger(eventId) &&
      eventId > 0 &&
      !this.measurement.trackingReceipt &&
      this.measurement.pending &&
      this.waiting.length < 100
    ) {
      const generation = this.generation;
      this.waiting.push(() => {
        if (
          event !== "event_viewed" ||
          (this.activeEvent === eventId && generation === this.generation)
        )
          this.journeyDestination(eventId, event, commerce);
      });
      return;
    }
    this.journeyDestination(eventId, event, commerce);
  }
  private journeyDestination(
    eventId: number,
    event: "event_viewed" | "cart_item_added" | "checkout_started",
    context: MarketingJourneyContext,
  ): void {
    if (
      !this.browser ||
      !this.measurement.advertising ||
      !Number.isSafeInteger(eventId) ||
      eventId <= 0
    )
      return;
    const receipt = this.measurement.trackingReceipt;
    if (!receipt) return;
    if (event === "event_viewed") {
      const key = `${receipt}:${eventId}:${this.generation}`;
      if (this.viewAttempts.has(key)) return;
      this.viewAttempts.add(key);
    }
    const generation = this.generation;
    const measurementGeneration = this.measurementGeneration;
    this.api
      .publicConfiguration(eventId, receipt)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((config) => {
        if (
          !config ||
          !this.measurement.advertising ||
          this.measurement.trackingReceipt !== receipt ||
          measurementGeneration !== this.measurementGeneration
        )
          return;
        // A confirmed cart/checkout action may finish validation after SPA navigation.
        // A view still belongs only to its original page lifetime; technical-session guards apply to all.
        if (
          event === "event_viewed" &&
          (this.activeEvent !== eventId || generation !== this.generation)
        )
          return;
        const names = {
          event_viewed: "ViewContent",
          cart_item_added: "AddToCart",
          checkout_started: "InitiateCheckout",
        };
        const actionId = crypto.randomUUID();
        for (const destination of config.destinations) {
          if (
            !isMarketingProvider(destination.provider) ||
            destination.browser_enabled === false ||
            !destination.allowed_events?.includes(event)
          )
            continue;
          this.send(
            destination,
            names[event],
            commercePayload(destination.provider, eventId, context, event),
            actionId,
            eventId,
          );
        }
      });
  }
  purchase(orderId: number): void {
    if (this.browser) this.measurement.ensure();
    if (
      this.browser &&
      Number.isSafeInteger(orderId) &&
      orderId > 0 &&
      !this.measurement.trackingReceipt &&
      this.measurement.pending &&
      this.waiting.length < 100
    ) {
      if (this.pendingOrders.has(orderId)) return;
      this.pendingOrders.add(orderId);
      const generation = this.generation;
      this.waiting.push(() => {
        this.pendingOrders.delete(orderId);
        if (generation === this.generation) this.purchase(orderId);
      });
      return;
    }
    if (
      !this.browser ||
      !this.measurement.advertising ||
      !Number.isSafeInteger(orderId) ||
      orderId <= 0
    )
      return;
    const receipt = this.measurement.trackingReceipt;
    if (!receipt) return;
    const generation = this.generation;
    this.api
      .descriptor(orderId, receipt)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((descriptor) => {
        if (
          !descriptor ||
          descriptor.order_id !== orderId ||
          !this.measurement.advertising ||
          this.measurement.trackingReceipt !== receipt ||
          generation !== this.generation ||
          !descriptor.browser_eligible_until ||
          Date.parse(descriptor.browser_eligible_until) <= Date.now()
        )
          return;
        for (const conversion of descriptor.conversions) {
          if (
            conversion.type !== "purchase_confirmed" ||
            !Number.isSafeInteger(conversion.ads_value_cents) ||
            conversion.ads_value_cents <= 0 ||
            !Number.isSafeInteger(conversion.event_id) ||
            conversion.event_id <= 0 ||
            !Number.isSafeInteger(conversion.commercial_units) ||
            conversion.commercial_units <= 0 ||
            !/^[A-Z]{3}$/.test(conversion.currency)
          )
            continue;
          const commerce = commerceContext({
            event_name: conversion.event_name,
            event_category: conversion.event_category,
            items: conversion.items,
            currency: conversion.currency,
            value_cents: conversion.ads_value_cents,
          });
          if (
            commerce.items?.reduce((sum, item) => sum + item.quantity, 0) !==
            conversion.commercial_units
          )
            delete commerce.items;
          for (const destination of conversion.destinations) {
            if (!isMarketingProvider(destination.provider)) continue;
            const id = destination.deduplication_id;
            if (!id) continue;
            const key = `${destination.provider}:${destination.public_destination_id}:${id}`;
            if (this.wasEmitted(key)) continue;
            if (
              this.send(
                destination,
                destination.provider_event || "Purchase",
                commercePayload(
                  destination.provider,
                  conversion.event_id,
                  commerce,
                  "purchase_confirmed",
                  conversion.commercial_units,
                ),
                id,
                conversion.event_id,
              )
            ) {
              this.emitted.add(key);
              try {
                sessionStorage.setItem(`yes7-pixel:${key}`, "1");
              } catch {
                /* Canonical backend identity remains the provider deduplication key. */
              }
            }
          }
        }
      });
  }
  private wasEmitted(key: string) {
    try {
      return (
        this.emitted.has(key) ||
        sessionStorage.getItem(`yes7-pixel:${key}`) === "1"
      );
    } catch {
      return this.emitted.has(key);
    }
  }
  private send(
    destination: MarketingDestination,
    event: string,
    properties: Record<string, unknown>,
    eventId: string,
    contentEventId: number,
  ): boolean {
    if (
      !isMarketingProvider(destination.provider) ||
      !this.measurement.advertising
    )
      return false;
    try {
      return this.adapters[destination.provider].send({
        destination,
        event,
        properties,
        eventId,
        contentEventId,
      });
    } catch {
      /* SDK/network blockers must never interfere with financial UI. */
    }
    return false;
  }
}
